import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, AlertTriangle, X, Repeat, DollarSign, Play, Pause, Search, TrendingUp, Check, Sparkles, Activity, Link, CheckSquare, Square } from 'lucide-react';
import apiService from '../../services/api';
import PageHeader from '../common/PageHeader';

const RecurringPaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recurrenceTypes, setRecurrenceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [linkingPayment, setLinkingPayment] = useState(null);  // Payment being linked to transactions
  const [showInactive, setShowInactive] = useState(false);
  const [summary, setSummary] = useState({ total_monthly: 0, active_count: 0 });

  useEffect(() => {
    loadData();
  }, [showInactive]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [paymentsData, accountsData, categoriesData, typesData] = await Promise.all([
        apiService.getRecurringPayments(!showInactive),
        apiService.getAccounts(),
        apiService.getCategories(),
        apiService.getRecurrenceTypes()
      ]);

      setPayments(paymentsData.payments || []);
      setSummary({
        total_monthly: paymentsData.total_monthly || 0,
        active_count: paymentsData.active_count || 0
      });
      setAccounts(accountsData);
      setCategories(categoriesData);
      setRecurrenceTypes(typesData);
    } catch (err) {
      console.error('Error loading recurring payments:', err);
      setError('Failed to load recurring payments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (paymentData) => {
    try {
      const newPayment = await apiService.createRecurringPayment(paymentData);
      await loadData();
      setIsAddModalOpen(false);
      // Automatically open Link Transactions modal for the new payment
      setLinkingPayment(newPayment);
    } catch (err) {
      console.error('Error creating recurring payment:', err);
      throw err;
    }
  };

  const handleUpdatePayment = async (paymentId, paymentData) => {
    try {
      await apiService.updateRecurringPayment(paymentId, paymentData);
      await loadData();
      setEditingPayment(null);
    } catch (err) {
      console.error('Error updating recurring payment:', err);
      throw err;
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      await apiService.deleteRecurringPayment(paymentId);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting recurring payment:', err);
      setError('Failed to delete recurring payment');
    }
  };

  const handleToggleActive = async (payment) => {
    try {
      await apiService.updateRecurringPayment(payment.id, {
        is_active: !payment.is_active
      });
      await loadData();
    } catch (err) {
      console.error('Error toggling payment status:', err);
      setError('Failed to update payment status');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getRecurrenceLabel = (type) => {
    const labels = {
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      semi_monthly: 'Semi-monthly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semi_annual: 'Semi-annual',
      annual: 'Annual',
      custom: 'Custom'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading recurring payments...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
              <div className="mt-4">
                <button
                  onClick={loadData}
                  className="text-sm font-medium text-red-800 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        icon={Repeat}
        title="Recurring Payments"
        subtitle="Manage your recurring bills and subscriptions"
      >
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Show inactive
        </label>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Add Payment</span>
        </button>
      </PageHeader>

      <div className="max-w-7xl mx-auto py-6 px-3 sm:px-4 lg:px-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Total</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {formatCurrency(summary.total_monthly)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Repeat className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Active Payments</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {summary.active_count}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payments List */}
        {payments.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Repeat className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 mb-2">No recurring payments</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Add your recurring bills and subscriptions to track them here
            </p>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Payment
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Frequency
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Monthly
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {payments.map((payment) => (
                    <tr key={payment.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${!payment.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3">
                            {payment.category_icon ? (
                              <span className="text-lg">{payment.category_icon}</span>
                            ) : (
                              <Repeat className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{payment.payee}</p>
                            {payment.category_name && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">{payment.category_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-600 dark:text-gray-400">{payment.account_name || '-'}</p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm text-gray-900 dark:text-white">{getRecurrenceLabel(payment.recurrence_type)}</p>
                          {payment.day_of_month && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Day {payment.day_of_month}
                              {payment.day_of_month_2 && ` & ${payment.day_of_month_2}`}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {payment.is_variable ? (
                          <div>
                            <div className="flex items-center justify-end text-sm font-semibold text-gray-900 dark:text-white">
                              <Activity className="h-3 w-3 mr-1 text-orange-500" title="Variable amount" />
                              {payment.avg_amount ? formatCurrency(payment.avg_amount) : formatCurrency(payment.amount)}
                            </div>
                            {payment.min_amount && payment.max_amount && payment.min_amount !== payment.max_amount && (
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                {formatCurrency(payment.min_amount)} - {formatCurrency(payment.max_amount)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(payment.amount)}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <p className="text-sm text-purple-600 dark:text-purple-400">
                          {formatCurrency(payment.monthly_amount)}
                          {payment.is_variable && (
                            <span className="ml-1 text-xs text-orange-500" title="Based on average">~</span>
                          )}
                        </p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleToggleActive(payment)}
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            payment.is_active
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}
                          title={payment.is_active ? 'Click to pause' : 'Click to activate'}
                        >
                          {payment.is_active ? (
                            <>
                              <Play className="h-3 w-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <Pause className="h-3 w-3 mr-1" />
                              Paused
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => setLinkingPayment(payment)}
                            className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                            title="Link transactions"
                          >
                            <Link className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setEditingPayment(payment)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="Edit payment"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(payment)}
                            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete payment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Payment Modal (with Pattern Detection) */}
      {isAddModalOpen && (
        <AddRecurringPaymentModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddPayment}
          accounts={accounts}
          categories={categories}
          recurrenceTypes={recurrenceTypes}
        />
      )}

      {/* Edit Payment Modal */}
      {editingPayment && (
        <RecurringPaymentModal
          isOpen={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          onSave={(data) => handleUpdatePayment(editingPayment.id, data)}
          payment={editingPayment}
          title="Edit Recurring Payment"
          accounts={accounts}
          categories={categories}
          recurrenceTypes={recurrenceTypes}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Delete Recurring Payment</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Are you sure you want to delete "{deleteConfirm.payee}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePayment(deleteConfirm.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Transactions Modal */}
      {linkingPayment && (
        <LinkTransactionsModal
          payment={linkingPayment}
          onClose={() => {
            setLinkingPayment(null);
            loadData();  // Refresh to show updated transaction counts
          }}
        />
      )}
    </div>
  );
};

// Add Recurring Payment Modal with Pattern Detection
const AddRecurringPaymentModal = ({ isOpen, onClose, onSave, accounts, categories, recurrenceTypes }) => {
  const [view, setView] = useState('detect'); // 'detect' or 'manual'
  const [patterns, setPatterns] = useState([]);
  const [loadingPatterns, setLoadingPatterns] = useState(true);
  const [selectedPattern, setSelectedPattern] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadPatterns();
    }
  }, [isOpen]);

  const loadPatterns = async () => {
    try {
      setLoadingPatterns(true);
      const result = await apiService.detectRecurringPatterns(3, 6);
      setPatterns(result.suggestions || []);
    } catch (err) {
      console.error('Error detecting patterns:', err);
      setPatterns([]);
    } finally {
      setLoadingPatterns(false);
    }
  };

  const handlePatternSelect = (pattern) => {
    setSelectedPattern(pattern);
    setView('manual');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 70) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
    if (confidence >= 40) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // If pattern selected, show the form
  if (view === 'manual') {
    return (
      <RecurringPaymentModal
        isOpen={true}
        onClose={() => {
          setSelectedPattern(null);
          setView('detect');
          onClose();
        }}
        onSave={onSave}
        title="Add Recurring Payment"
        accounts={accounts}
        categories={categories}
        recurrenceTypes={recurrenceTypes}
        initialData={selectedPattern ? {
          payee: selectedPattern.payee,
          amount: selectedPattern.average_amount,
          account_id: selectedPattern.account_id,
          category_id: selectedPattern.category_id,
          recurrence_type: selectedPattern.suggested_recurrence
        } : null}
        onBack={() => {
          setSelectedPattern(null);
          setView('detect');
        }}
      />
    );
  }

  // Pattern detection view
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <Sparkles className="h-5 w-5 text-purple-500 mr-2" />
              Add Recurring Payment
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Select a detected pattern or create manually
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingPatterns ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Analyzing transactions...</span>
            </div>
          ) : patterns.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-2">No recurring patterns detected</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
                We couldn't find any recurring payment patterns in your transactions.
                <br />You can still create one manually.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Found {patterns.length} potential recurring payment{patterns.length !== 1 ? 's' : ''} in your transactions:
              </p>
              {patterns.map((pattern, index) => (
                <button
                  key={index}
                  onClick={() => handlePatternSelect(pattern)}
                  className="w-full text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <p className="font-medium text-gray-900 dark:text-white">{pattern.payee}</p>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${getConfidenceColor(pattern.confidence)}`}>
                          {Math.round(pattern.confidence)}% match
                        </span>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400 flex-wrap gap-x-3">
                        <span>{pattern.suggested_recurrence_label}</span>
                        <span>•</span>
                        <span>{pattern.occurrence_count} transactions</span>
                        <span>•</span>
                        <span>{pattern.account_name}</span>
                      </div>
                      {pattern.category_name && (
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                          Category: {pattern.category_name}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-semibold text-purple-600 dark:text-purple-400">
                        {formatCurrency(pattern.average_amount)}
                      </p>
                      {pattern.min_amount !== pattern.max_amount && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {formatCurrency(pattern.min_amount)} - {formatCurrency(pattern.max_amount)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center text-xs text-purple-600 dark:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Check className="h-3 w-3 mr-1" />
                    Click to use as template
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
          <div className="flex justify-between">
            <button
              onClick={() => {
                setSelectedPattern(null);
                setView('manual');
              }}
              className="px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
            >
              + Create manually
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Recurring Payment Modal Component (for editing or manual creation)
const RecurringPaymentModal = ({ isOpen, onClose, onSave, payment, title, accounts, categories, recurrenceTypes, initialData, onBack }) => {
  const [formData, setFormData] = useState({
    payee: initialData?.payee || payment?.payee || '',
    amount: initialData?.amount || payment?.amount || '',
    account_id: initialData?.account_id || payment?.account_id || '',
    category_id: initialData?.category_id || payment?.category_id || '',
    recurrence_type: initialData?.recurrence_type || payment?.recurrence_type || 'monthly',
    day_of_month: payment?.day_of_month || 1,
    day_of_month_2: payment?.day_of_month_2 || '',
    custom_interval_days: payment?.custom_interval_days || '',
    is_active: payment?.is_active ?? true,
    is_variable: initialData?.is_variable || payment?.is_variable || false,
    start_date: payment?.start_date || '',
    end_date: payment?.end_date || '',
    notes: payment?.notes || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        account_id: formData.account_id ? parseInt(formData.account_id) : null,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        day_of_month: formData.day_of_month ? parseInt(formData.day_of_month) : null,
        day_of_month_2: formData.day_of_month_2 ? parseInt(formData.day_of_month_2) : null,
        custom_interval_days: formData.custom_interval_days ? parseInt(formData.custom_interval_days) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };
      await onSave(submitData);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save recurring payment');
    } finally {
      setLoading(false);
    }
  };

  const showDayOfMonth = ['monthly', 'quarterly', 'semi_annual', 'annual'].includes(formData.recurrence_type);
  const showDayOfMonth2 = formData.recurrence_type === 'semi_monthly';
  const showCustomInterval = formData.recurrence_type === 'custom';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            {onBack && (
              <button
                onClick={onBack}
                className="mr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Payee *
              </label>
              <input
                type="text"
                value={formData.payee}
                onChange={(e) => setFormData({ ...formData, payee: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Netflix, Electric Company"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount {formData.is_variable ? '(base)' : '*'}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0.00"
                  required
                />
              </div>
              {formData.is_variable && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This is a fallback amount. Actual monthly will be calculated from linked transactions.
                </p>
              )}
            </div>

            <div className="flex items-center p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
              <input
                type="checkbox"
                id="is_variable"
                checked={formData.is_variable}
                onChange={(e) => setFormData({ ...formData, is_variable: e.target.checked })}
                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label htmlFor="is_variable" className="ml-2 flex-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <Activity className="h-4 w-4 mr-1 text-orange-500" />
                  Variable Amount
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Monthly total will be calculated from transaction history average (e.g., utilities)
                </p>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account *
                </label>
                <select
                  value={formData.account_id}
                  onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">Select account</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Frequency *
              </label>
              <select
                value={formData.recurrence_type}
                onChange={(e) => setFormData({ ...formData, recurrence_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                {recurrenceTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {showDayOfMonth && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Day of Month
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.day_of_month}
                  onChange={(e) => setFormData({ ...formData, day_of_month: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}

            {showDayOfMonth2 && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Day
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.day_of_month}
                    onChange={(e) => setFormData({ ...formData, day_of_month: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Second Day
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.day_of_month_2}
                    onChange={(e) => setFormData({ ...formData, day_of_month_2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., 15"
                  />
                </div>
              </div>
            )}

            {showCustomInterval && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Interval (days)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.custom_interval_days}
                  onChange={(e) => setFormData({ ...formData, custom_interval_days: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., 90 for quarterly"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Optional notes..."
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Active
              </label>
            </div>
          </form>
        </div>

        {/* Sticky Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="payment-form"
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Link Transactions Modal Component
const LinkTransactionsModal = ({ payment, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadTransactions(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, payment.id]);

  const loadTransactions = async (search) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.searchTransactionsForPayment(payment.id, search);
      setTransactions(result.transactions || []);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (txnId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txnId)) {
        newSet.delete(txnId);
      } else {
        newSet.add(txnId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const unlinkedIds = transactions.filter(t => !t.is_linked).map(t => t.id);
    setSelectedIds(new Set(unlinkedIds));
  };

  const handleSelectNone = () => {
    setSelectedIds(new Set());
  };

  const handleLinkSelected = async () => {
    if (selectedIds.size === 0) return;

    try {
      setSaving(true);
      await apiService.bulkLinkTransactions(payment.id, Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadTransactions(searchTerm);
    } catch (err) {
      console.error('Error linking transactions:', err);
      setError('Failed to link transactions');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkSelected = async () => {
    const linkedAndSelected = transactions
      .filter(t => t.is_linked && selectedIds.has(t.id))
      .map(t => t.id);

    if (linkedAndSelected.length === 0) {
      // If no linked selected, unlink all linked
      const allLinked = transactions.filter(t => t.is_linked).map(t => t.id);
      if (allLinked.length === 0) return;

      try {
        setSaving(true);
        await apiService.bulkUnlinkTransactions(payment.id, allLinked);
        await loadTransactions(searchTerm);
      } catch (err) {
        console.error('Error unlinking transactions:', err);
        setError('Failed to unlink transactions');
      } finally {
        setSaving(false);
      }
    } else {
      try {
        setSaving(true);
        await apiService.bulkUnlinkTransactions(payment.id, linkedAndSelected);
        setSelectedIds(new Set());
        await loadTransactions(searchTerm);
      } catch (err) {
        console.error('Error unlinking transactions:', err);
        setError('Failed to unlink transactions');
      } finally {
        setSaving(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };

  const linkedCount = transactions.filter(t => t.is_linked).length;
  const unlinkedCount = transactions.filter(t => !t.is_linked).length;

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <Link className="h-5 w-5 text-green-500 mr-2" />
              Link Transactions
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Link transactions to <span className="font-medium">{payment.payee}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search transactions (defaults to "${payment.payee}")`}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Selection Controls */}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center space-x-3 text-sm">
              <button
                onClick={handleSelectAll}
                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
              >
                Select all unlinked
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                onClick={handleSelectNone}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Clear selection
              </button>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <span className="text-green-600 dark:text-green-400 font-medium">{linkedCount}</span> linked,{' '}
              <span className="text-gray-600 dark:text-gray-400">{unlinkedCount}</span> available
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-green-500" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Searching transactions...</span>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No transactions found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Try a different search term
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((txn) => (
                <div
                  key={txn.id}
                  onClick={() => handleToggleSelect(txn.id)}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    txn.is_linked
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : selectedIds.has(txn.id)
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {/* Checkbox */}
                  <div className="mr-3">
                    {txn.is_linked ? (
                      <CheckSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : selectedIds.has(txn.id) ? (
                      <CheckSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <Square className="h-5 w-5 text-gray-400" />
                    )}
                  </div>

                  {/* Transaction Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {txn.description}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {txn.account_name} • {formatDate(txn.transaction_date)}
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="ml-3 text-right">
                    <p className={`text-sm font-semibold ${
                      txn.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                    }`}>
                      {txn.amount < 0 ? '-' : '+'}{formatCurrency(txn.amount)}
                    </p>
                  </div>

                  {/* Linked Badge */}
                  {txn.is_linked && (
                    <div className="ml-3">
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded-full">
                        Linked
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {selectedIds.size > 0 && (
                <span>{selectedIds.size} selected</span>
              )}
            </div>
            <div className="flex space-x-3">
              {linkedCount > 0 && (
                <button
                  onClick={handleUnlinkSelected}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-800 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  {saving ? 'Unlinking...' : 'Unlink All'}
                </button>
              )}
              <button
                onClick={handleLinkSelected}
                disabled={saving || selectedIds.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Linking...' : `Link Selected (${selectedIds.size})`}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecurringPaymentsTab;
